import { BookingProvider, Path, Signal } from "@common/constans";
import { IPnrInfoRequest, PnrInfoRequest } from "@common/models";
import { Culture, IModelValidationMessage, IValidationError } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import Abort from "@service/Abort";
import Config from "@service/Config";
import { Action, pnrInformation } from "@service/proxy";
import Translate from "@service/Translate";
import { formatToDateTime } from "@utilize/Format";
import { handleRSVError, nameof, validationMessage } from "@utilize/Util";
import { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import Button from "./general/form/Button";
import DateTimeInput from "./general/form/DateTimeInput";
import Form from "./general/form/Form";
import TextInput from "./general/form/TextInput";
import { CreateStep } from "./Stepper";
interface IReservationStatusFormProps {
    orientation: "horizontal" | "vertical";
}

const ReservationStatusForm: FC<IReservationStatusFormProps> = ({ orientation }) => {
    const { tr } = Translate;
    const { push } = useHistory();
    const dispatch = useDispatch();
    const { lang, code } = Culture.getLocale();
    const [showSearchLoading, setShowSearchLoading] = useState<boolean>(false);
    const [reservationStatus, setReservationStatus] = useState<IPnrInfoRequest>({});
    const [validationError, setValidationError] = useState<IValidationError>({});

    const formErrorsMessages: IModelValidationMessage = {
        pnr: {
            required: tr("err_record_required", "reservation_number"),
            type: tr("err_record_force_digit", `${tr("reservation_number")} / ${tr("ticket_number")}`),
            pattern: tr("err_invalid_pnr")
        },
        contactLastName: {
            required: tr("err_record_required", `${tr("last_name")} ${tr("passenger")}`),
            pattern: tr("err_invalid_flight_number"),
        },
        departureDate: {
            required: tr("err_record_required", "flight_date")
        }
    };

    const removeValidationError = (name: string) => {
        if (validationError[name])
            delete validationError[name];
        setValidationError({ ...validationError });
    }

    const onChange = (name: string, value: any) => {
        removeValidationError(name);
    };

    const onBlur = (name: string, value: any) => {
        reservationStatus[name as keyof IPnrInfoRequest] = value;
        removeValidationError(name);
        setReservationStatus({ ...reservationStatus });
    }

    const checkinRequestValidation = () => {
        let validationResult;
        validationResult = new PnrInfoRequest(reservationStatus).validate() || {};
        return validationResult;
    };

    const onPnrInfoSearch = () => {
        const vaildateError = checkinRequestValidation();
        if (Object.keys(vaildateError).length)
            return setValidationError(vaildateError);

        setShowSearchLoading(true);
        if (Config.config.defaultBookingProvider === BookingProvider.Mahan) {
            pnrInformation(Action.POST, { data: reservationStatus, signal: Abort.get(Signal.Pnr).signal })
                .then((response) => {
                    const pnrInfo = response.content;
                    dispatch<IAppAction>({ type: AppAction.PnrInfo, payload: { pnrInfo } });
                    dispatch<IAppAction>({ type: AppAction.ShowBackButton, payload: { showBackButton: false } });
                    dispatch<IAppAction>({ type: AppAction.CurrentStep, payload: { currentStep: CreateStep.Search } });
                    dispatch<IAppAction>({ type: AppAction.Locale, payload: { locale: code } });
                    push(`${Path.Booking.ManageBooking}?pnr=${reservationStatus.pnr}&name=${reservationStatus.contactLastName}&date=${reservationStatus.departureDate}`);
                })
                .catch((err) => {
                    handleRSVError(err);
                    setShowSearchLoading(false);
                })
        } else {
            const depDate = formatToDateTime(reservationStatus.departureDate || 0, { format: "Y-m-d", locale: "en-US", utc: false });
            const url = `https://reservations.mahan.aero/service-app/ibe/reservation.html#/modify/reservations/lookup/${lang}/IRR/${reservationStatus.pnr}/${reservationStatus.contactLastName}/${depDate}`;
            window.location.href = url;
        }
    }

    const error = validationError ? validationMessage(formErrorsMessages, validationError) : {};

    return (
        <Form onSubmit={showSearchLoading ? undefined : onPnrInfoSearch}>
            <div className={`row flex spa-1 ${orientation}`}>
                <div className={`${orientation === "horizontal" ? "col-1-4 col-md-1-2" : "col-1-1"} col-sm-1-1`}>
                    <TextInput
                        name={nameof<IPnrInfoRequest>("pnr")}
                        value={reservationStatus.pnr}
                        autoComplete="off"
                        type="number"
                        onChange={onChange}
                        onBlur={onBlur}
                        label={tr("reservation_number")}
                        required
                        error={error.pnr}
                        dir="ltr"
                    />
                </div>
                <div className={`${orientation === "horizontal" ? "col-1-4 col-md-1-2" : "col-1-1"} col-sm-1-1`}>
                    <TextInput
                        name={nameof<IPnrInfoRequest>("contactLastName")}
                        value={reservationStatus.contactLastName}
                        autoComplete='off'
                        onChange={onChange}
                        onBlur={onBlur}
                        label={`${tr("last_name_en")} ${tr("passenger")}`}
                        error={error.contactLastName}
                        required
                        dir="ltr"
                    />
                </div>
                <div className={`${orientation === "horizontal" ? "col-1-4 col-md-1-2" : "col-1-1"} col-sm-1-1`}>
                    <DateTimeInput
                        name={nameof<IPnrInfoRequest>("departureDate")}
                        value={reservationStatus.departureDate}
                        label={tr("flight_date")}
                        required
                        error={error.departureDate}
                        onChange={onBlur}
                    />
                </div>
                <div className={`${orientation === "horizontal" ? "col-1-4 col-md-1-2" : "col-1-1"} col-sm-1-1`}>
                    <Button
                        text={tr("search")}
                        variant="contained"
                        color="secondary"
                        size="large"
                        disabled={showSearchLoading}
                        type="submit"
                        loading={showSearchLoading}
                    />
                </div>
            </div>
        </Form>
    )
}

export default ReservationStatusForm;